import { ResponsiveTable, ResponsiveCell } from "./ResponsiveTable";
import type { StockMovement, StockMovementReason } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { useAsync } from "../lib/useAsync";
import { formatDate } from "../lib/format";
import { Spinner } from "./Spinner";
import { ErrorBanner } from "./ErrorBanner";

const REASON_META: Record<StockMovementReason, { label: string; className: string }> = {
  initial: { label: "Initial", className: "badge badge--muted" },
  receive: { label: "Receive", className: "badge badge--success" },
  return: { label: "Return", className: "badge badge--success" },
  sell: { label: "Sell", className: "badge badge--warning" },
  adjust: { label: "Adjust", className: "badge badge--muted" },
  correction: { label: "Correction", className: "badge badge--muted" },
};

interface ProductMovementsProps {
  productId: string;
  /** Bump this to force a reload after a stock adjustment. */
  reloadToken: number;
}

export function ProductMovements({ productId, reloadToken }: ProductMovementsProps) {
  const api = useApi();
  // reloadToken is in deps so the parent can refresh the trail after an adjust.
  const { data, loading, error, reload } = useAsync<StockMovement[]>(
    () => api.listMovements(productId),
    [api, productId, reloadToken],
  );

  if (loading) return <Spinner label="Loading movements…" />;
  if (error) return <ErrorBanner message={error.message} onRetry={reload} />;

  const movements = data ?? [];
  if (movements.length === 0) {
    return <p className="muted">No stock movements recorded yet.</p>;
  }

  return (
    <ResponsiveTable label="Stock movements">
      <thead role="rowgroup">
        <tr role="row">
          <th scope="col" role="columnheader">Date</th>
          <th scope="col" role="columnheader">Reason</th>
          <th scope="col" role="columnheader">Change</th>
          <th scope="col" role="columnheader">Resulting qty</th>
          <th scope="col" role="columnheader">By</th>
          <th scope="col" role="columnheader">Note</th>
        </tr>
      </thead>
      <tbody role="rowgroup">
        {movements.map((m) => {
          const meta = REASON_META[m.reason];
          return (
            <tr role="row" key={m.id}>
              <ResponsiveCell label="Date">{formatDate(m.createdAt)}</ResponsiveCell>
              <ResponsiveCell label="Reason">
                <span className={meta.className}>{meta.label}</span>
              </ResponsiveCell>
              <ResponsiveCell label="Change">{m.delta > 0 ? `+${m.delta}` : m.delta}</ResponsiveCell>
              <ResponsiveCell label="Resulting qty">{m.quantityAfter}</ResponsiveCell>
              <ResponsiveCell label="By">{m.createdBy ?? "—"}</ResponsiveCell>
              <ResponsiveCell label="Note">{m.note ?? "—"}</ResponsiveCell>
            </tr>
          );
        })}
      </tbody>
    </ResponsiveTable>
  );
}
